import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { api, API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { GlassCard } from "@/components/common/GlassCard";
import { PageHeader } from "@/components/common/PageHeader";
import { StatusPill } from "@/components/common/StatusPill";
import { LoadingState, EmptyState } from "@/components/common/States";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FolderOpen, Upload, Trash2, FileText, Film, Stethoscope, FileSignature, ExternalLink, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";
import { PlayerSubNav } from "@/components/common/PlayerSubNav";

function useResolvedPlayerId() {
  const { user } = useAuth();
  const { playerId } = useParams();
  return playerId || user?.player_id;
}

const typeIcon = { medical: Stethoscope, film: Film, contract: FileSignature, other: FileText };
const typeTone = { medical: "emerald", film: "cyan", contract: "amber", other: "neutral" };

export default function DocumentsPage() {
  const { user, token } = useAuth();
  const playerId = useResolvedPlayerId();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [uploading, setUploading] = useState(false);
  const [name, setName] = useState("");
  const [type, setType] = useState("other");
  const [showLink, setShowLink] = useState(false);
  const [linkForm, setLinkForm] = useState({ name: "", link_url: "", type: "film" });
  const fileRef = useRef(null);

  const load = () => {
    if (!playerId) { setLoading(false); return; }
    setLoading(true);
    api.get("/documents", { params: { player_id: playerId } }).then((r) => setDocs(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, [playerId]);

  const onUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("player_id", playerId);
    fd.append("name", name || file.name);
    fd.append("type", type);
    fd.append("file", file);
    try {
      const r = await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDocs((arr) => [r.data, ...arr]);
      setName("");
      toast.success("File uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const addLink = async () => {
    if (!linkForm.link_url) return toast.error("URL required");
    try {
      const r = await api.post("/documents/link", { ...linkForm, player_id: playerId });
      setDocs((arr) => [r.data, ...arr]);
      setLinkForm({ name: "", link_url: "", type: "film" });
      setShowLink(false);
      toast.success("Link added");
    } catch (e) { toast.error("Failed"); }
  };

  const remove = async (id) => {
    await api.delete(`/documents/${id}`);
    setDocs((arr) => arr.filter((d) => d.id !== id));
  };

  const filtered = docs.filter((d) => filter === "all" || d.type === filter);

  const downloadFile = async (doc) => {
    if (doc.is_link) { window.open(doc.link_url, "_blank"); return; }
    try {
      const url = `${API}${doc.file_url.replace(/^\/api/, "")}`;
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error("download failed");
      const blob = await r.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl; a.download = doc.name; a.click();
      URL.revokeObjectURL(objectUrl);
    } catch (e) { toast.error("Download failed"); }
  };

  if (loading) return <LoadingState />;
  return (
    <div className="max-w-[1400px] mx-auto">
      <PlayerSubNav />
      <PageHeader
        title="Documents"
        subtitle="Medicals, film links, and contract templates kept in one secure place."
        actions={
          user?.role === "admin" && (
            <div className="flex gap-2">
              <Button data-testid="docs-add-link" onClick={() => setShowLink((v) => !v)} variant="ghost" className="text-2 hover:text-white"><LinkIcon className="h-4 w-4" /> Add link</Button>
            </div>
          )
        }
      />
      {user?.role === "admin" && (
        <GlassCard className="p-5 mb-5" testId="documents-upload-dropzone">
          <div className="grid md:grid-cols-3 gap-3 items-end">
            <div className="space-y-1">
              <Label className="text-2 text-xs">Display name</Label>
              <Input data-testid="docs-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Pro Day Film" className="bg-white/5 border-white/10 text-1" />
            </div>
            <div className="space-y-1">
              <Label className="text-2 text-xs">Type</Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger data-testid="docs-type" className="bg-white/5 border-white/10 text-1"><SelectValue /></SelectTrigger>
                <SelectContent className="glass-strong border-white/10">
                  <SelectItem value="medical">Medical</SelectItem>
                  <SelectItem value="film">Film</SelectItem>
                  <SelectItem value="contract">Contract</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-2 text-xs">File</Label>
              <input data-testid="docs-file" ref={fileRef} type="file" onChange={(e) => onUpload(e.target.files?.[0])} className="text-2 text-sm w-full" />
            </div>
          </div>
          {uploading && <div className="mt-2 text-2 text-xs">Uploading…</div>}
        </GlassCard>
      )}
      {showLink && user?.role === "admin" && (
        <GlassCard className="p-5 mb-5" testId="docs-link-form">
          <div className="grid md:grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label className="text-2 text-xs">Name</Label>
              <Input value={linkForm.name} onChange={(e) => setLinkForm((f) => ({ ...f, name: e.target.value }))} className="bg-white/5 border-white/10 text-1" />
            </div>
            <div className="space-y-1">
              <Label className="text-2 text-xs">Type</Label>
              <Select value={linkForm.type} onValueChange={(v) => setLinkForm((f) => ({ ...f, type: v }))}>
                <SelectTrigger className="bg-white/5 border-white/10 text-1"><SelectValue /></SelectTrigger>
                <SelectContent className="glass-strong border-white/10">
                  <SelectItem value="medical">Medical</SelectItem>
                  <SelectItem value="film">Film</SelectItem>
                  <SelectItem value="contract">Contract</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-2 text-xs">URL</Label>
              <Input value={linkForm.link_url} onChange={(e) => setLinkForm((f) => ({ ...f, link_url: e.target.value }))} placeholder="https://hudl.com/…" className="bg-white/5 border-white/10 text-1" />
            </div>
          </div>
          <div className="flex justify-end mt-3"><Button data-testid="docs-link-save" onClick={addLink} className="cta-primary">Save link</Button></div>
        </GlassCard>
      )}
      <div className="flex items-center gap-2 mb-3">
        {["all", "medical", "film", "contract", "other"].map((t) => (
          <button
            key={t}
            data-testid={`documents-filter-${t}`}
            onClick={() => setFilter(t)}
            className={`rounded-full px-3 py-1.5 text-xs ring-1 ${filter === t ? "ring-cyan-300/40 bg-cyan-300/10 text-cyan-200" : "ring-white/10 text-2 hover:text-white"}`}
          >
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>
      {filtered.length === 0 ? (
        <EmptyState icon={FolderOpen} title="No documents" description="Upload films, medicals, or other files." />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((d) => {
            const Icon = typeIcon[d.type] || FileText;
            return (
              <GlassCard key={d.id} hover className="p-4" testId={`document-row-${d.id}`}>
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-xl bg-white/5 ring-1 ring-white/10 grid place-items-center"><Icon className="h-4 w-4 text-cyan-300" /></div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-1 font-medium truncate">{d.name}</div>
                      <StatusPill tone={typeTone[d.type] || "neutral"}>{d.type}</StatusPill>
                    </div>
                    <div className="text-3 text-[11px] mt-0.5 truncate">{d.uploaded_by || "—"} · {new Date(d.created_at).toLocaleDateString()}</div>
                    <div className="flex items-center gap-2 mt-3">
                      <Button onClick={() => downloadFile(d)} size="sm" variant="ghost" className="text-2 hover:text-white">
                        {d.is_link ? <ExternalLink className="h-3.5 w-3.5" /> : <Upload className="h-3.5 w-3.5 rotate-180" />}
                        {d.is_link ? "Open" : "Download"}
                      </Button>
                      {user?.role === "admin" && (
                        <Button onClick={() => remove(d.id)} size="sm" variant="ghost" className="text-rose-300 ml-auto"><Trash2 className="h-3.5 w-3.5" /></Button>
                      )}
                    </div>
                  </div>
                </div>
              </GlassCard>
            );
          })}
        </div>
      )}
    </div>
  );
}
